import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { API_BASE_URL } from '../../../core/config/api-base-url.token';
import { NumeronCollectionModel } from '../models/numeron.model';

const VISIBILITY_ENABLED_VALUE = '1';
const VISIBILITY_DISABLED_VALUE = '0';
const COLLECTION_MODE_ENABLED_VALUE = '16';
const COLLECTION_MODE_DISABLED_VALUE = '0';

export interface UpdateNumeronPayload {
  collectionId: string;
  numeral: string;
  title: string;
  topicId: string;
  collectionType: string;
  source: string;
  url: string;
  categories: string;
  relatedContentURL?: string;
  visibility: boolean;
  modeEnabled: boolean;
  file: File | null;
  assetPath: string;
  subType: string;
}

export type CreateNumeronPayload = Omit<UpdateNumeronPayload, 'collectionId'>;

export interface AddNumeronToCollectionPayload {
  collectionId: string;
  collectionTitle: string;
  topicId: string;
  collectionType: string;
  collectionSource: string;
  collectionURL: string;
  collectionCategories: string;
  relatedContentURL: string;
  collectionAssetPath: string;
  collectionVisibility: boolean;
  collectionModeEnabled: boolean;
  numeral: string;
  title: string;
  source: string;
  url: string;
  categories: string;
  file: File | null;
  assetPath: string;
  subType: string;
  numeronActions: string;
}

export interface UpdateCollectionNumeronPayload extends AddNumeronToCollectionPayload {
  numeronId: string;
}

export interface CreateCollectionPayload {
  collectionTitle: string;
  collectionSource: string;
  collectionURL: string;
  collectionCategories: string;
  relatedContentURL: string;
  collectionType: string;
  collectionAssetPath: string;
  collectionVisibility: boolean;
  collabMode: boolean;
  anonMode: boolean;
  topicId: string;
  collectionActions: string;
}

export interface UpdateCollectionPayload extends CreateCollectionPayload {
  collectionId: string;
  numeronId: string;
  numeral: string;
  title: string;
  source: string;
  url: string;
  categories: string;
  assetPath: string;
  subType: string;
  numeronActions: string;
}

function buildUpdateNumeronFormData(payload: CreateNumeronPayload): FormData {
  const formData = new FormData();
  const normalizedAssetPath = payload.assetPath.trim();
  const normalizedSubType = payload.subType.trim();

  formData.append('numeral', payload.numeral.trim());
  formData.append('title', payload.title.trim());
  formData.append('topicId', payload.topicId.trim());
  formData.append('collectionType', payload.collectionType);
  formData.append('source', payload.source.trim());
  formData.append('url', payload.url.trim());
  formData.append('categories', payload.categories.trim());
  if (payload.relatedContentURL !== undefined) {
    formData.append('relatedContentURL', payload.relatedContentURL.trim());
  }
  formData.append(
    'collectionVisibility',
    payload.visibility ? VISIBILITY_ENABLED_VALUE : VISIBILITY_DISABLED_VALUE,
  );
  formData.append(
    'visibility',
    payload.visibility ? VISIBILITY_ENABLED_VALUE : VISIBILITY_DISABLED_VALUE,
  );
  formData.append(
    'collectionMode',
    payload.modeEnabled ? COLLECTION_MODE_ENABLED_VALUE : COLLECTION_MODE_DISABLED_VALUE,
  );
  formData.append(
    'mode',
    payload.modeEnabled ? COLLECTION_MODE_ENABLED_VALUE : COLLECTION_MODE_DISABLED_VALUE,
  );

  if (payload.file) {
    formData.append('file', payload.file);
    return formData;
  }

  if (normalizedAssetPath) {
    formData.append('assetPath', normalizedAssetPath);
    return formData;
  }

  if (normalizedSubType) {
    formData.append('assetPath', '');
    formData.append('subType', normalizedSubType);
  }

  return formData;
}

function buildCreateNumeronFormData(payload: CreateNumeronPayload): FormData {
  const formData = new FormData();
  const normalizedAssetPath = payload.assetPath.trim();
  const normalizedSubType = payload.subType.trim();

  formData.append('numeral', payload.numeral.trim());
  formData.append('title', payload.title.trim());
  formData.append('topicId', payload.topicId.trim());
  formData.append('source', payload.source.trim());
  formData.append('url', payload.url.trim());
  formData.append('categories', payload.categories.trim());

  if (payload.file) {
    formData.append('file', payload.file);
  }

  if (normalizedAssetPath) {
    formData.append('assetPath', normalizedAssetPath);
  }

  if (normalizedSubType) {
    formData.append('subType', normalizedSubType);
  }

  formData.append(
    'collectionVisibility',
    payload.visibility ? VISIBILITY_ENABLED_VALUE : VISIBILITY_DISABLED_VALUE,
  );
  formData.append(
    'visibility',
    payload.visibility ? VISIBILITY_ENABLED_VALUE : VISIBILITY_DISABLED_VALUE,
  );
  formData.append(
    'mode',
    payload.modeEnabled ? COLLECTION_MODE_ENABLED_VALUE : COLLECTION_MODE_DISABLED_VALUE,
  );

  return formData;
}

function buildCreateCollectionFormData(payload: CreateCollectionPayload): FormData {
  const formData = new FormData();

  formData.append('collectionTitle', payload.collectionTitle.trim());
  if (payload.collectionSource.trim()) {
    formData.append('collectionSource', payload.collectionSource.trim());
  }
  if (payload.collectionURL.trim()) {
    formData.append('collectionURL', payload.collectionURL.trim());
  }
  if (payload.collectionCategories.trim()) {
    formData.append('collectionCategories', payload.collectionCategories.trim());
  }
  if (payload.relatedContentURL.trim()) {
    formData.append('relatedContentURL', payload.relatedContentURL.trim());
  }
  if (payload.collectionType.trim()) {
    formData.append('collectionType', payload.collectionType.trim());
  }
  if (payload.collectionAssetPath.trim()) {
    formData.append('collectionAssetPath', payload.collectionAssetPath.trim());
  }

  formData.append(
    'collectionVisibility',
    payload.collectionVisibility ? VISIBILITY_ENABLED_VALUE : VISIBILITY_DISABLED_VALUE,
  );
  formData.append(
    'collectionMode',
    payload.collabMode
      ? payload.anonMode
        ? '17'
        : COLLECTION_MODE_ENABLED_VALUE
      : payload.anonMode
        ? '1'
        : COLLECTION_MODE_DISABLED_VALUE,
  );
  formData.append('collectionActions', payload.collectionActions);
  formData.append('topicId', payload.topicId.trim());

  return formData;
}

function buildAddNumeronToCollectionFormData(payload: AddNumeronToCollectionPayload): FormData {
  const formData = new FormData();
  const normalizedAssetPath = payload.assetPath.trim();
  const normalizedSubType = payload.subType.trim();

  formData.append('collectionTitle', payload.collectionTitle.trim());
  formData.append('topicId', payload.topicId.trim());
  formData.append('collectionType', payload.collectionType.trim());

  if (payload.collectionSource.trim()) {
    formData.append('collectionSource', payload.collectionSource.trim());
  }
  if (payload.collectionURL.trim()) {
    formData.append('collectionURL', payload.collectionURL.trim());
  }
  if (payload.collectionCategories.trim()) {
    formData.append('collectionCategories', payload.collectionCategories.trim());
  }
  if (payload.relatedContentURL.trim()) {
    formData.append('relatedContentURL', payload.relatedContentURL.trim());
  }
  if (payload.collectionAssetPath.trim()) {
    formData.append('collectionAssetPath', payload.collectionAssetPath.trim());
  }

  formData.append(
    'collectionVisibility',
    payload.collectionVisibility ? VISIBILITY_ENABLED_VALUE : VISIBILITY_DISABLED_VALUE,
  );
  formData.append(
    'collectionMode',
    payload.collectionModeEnabled ? COLLECTION_MODE_ENABLED_VALUE : COLLECTION_MODE_DISABLED_VALUE,
  );

  formData.append('numeral', payload.numeral.trim());
  formData.append('title', payload.title.trim());

  if (payload.source.trim()) {
    formData.append('source', payload.source.trim());
  }
  if (payload.url.trim()) {
    formData.append('url', payload.url.trim());
  }
  if (payload.categories.trim()) {
    formData.append('categories', payload.categories.trim());
  }

  if (payload.file) {
    formData.append('file', payload.file);
  } else if (normalizedAssetPath) {
    formData.append('assetPath', normalizedAssetPath);
  } else if (normalizedSubType) {
    formData.append('subType', normalizedSubType);
  }

  formData.append('numeronActions', payload.numeronActions);

  return formData;
}

function buildUpdateCollectionFormData(payload: UpdateCollectionPayload): FormData {
  const formData = new FormData();

  formData.append('collectionTitle', payload.collectionTitle.trim());
  if (payload.collectionSource.trim()) {
    formData.append('collectionSource', payload.collectionSource.trim());
  }
  formData.append('topicId', payload.topicId.trim());
  formData.append('collectionType', payload.collectionType.trim());
  formData.append('edittype', 'collection');

  if (payload.collectionURL.trim()) {
    formData.append('collectionURL', payload.collectionURL.trim());
  }
  if (payload.collectionCategories.trim()) {
    formData.append('collectionCategories', payload.collectionCategories.trim());
  }
  if (payload.relatedContentURL.trim()) {
    formData.append('relatedContentURL', payload.relatedContentURL.trim());
  }
  if (payload.collectionAssetPath.trim()) {
    formData.append('collectionAssetPath', payload.collectionAssetPath.trim());
  }

  formData.append(
    'collectionVisibility',
    payload.collectionVisibility ? VISIBILITY_ENABLED_VALUE : VISIBILITY_DISABLED_VALUE,
  );
  formData.append(
    'mode',
    payload.collabMode || payload.anonMode
      ? COLLECTION_MODE_ENABLED_VALUE
      : COLLECTION_MODE_DISABLED_VALUE,
  );
  formData.append('collectionActions', payload.collectionActions);

  formData.append('numeral', payload.numeral.trim());
  formData.append('title', payload.title.trim());

  if (payload.source.trim()) {
    formData.append('source', payload.source.trim());
  }
  if (payload.url.trim()) {
    formData.append('url', payload.url.trim());
  }
  if (payload.categories.trim()) {
    formData.append('categories', payload.categories.trim());
  }
  if (payload.assetPath.trim()) {
    formData.append('assetPath', payload.assetPath.trim());
  }
  if (payload.subType.trim()) {
    formData.append('subType', payload.subType.trim());
  }

  formData.append('numeronActions', payload.numeronActions);

  return formData;
}

@Injectable({
  providedIn: 'root',
})
export class NumeronsService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiBaseUrl = inject(API_BASE_URL);

  public getNumeronCollection(collectionId: string): Observable<NumeronCollectionModel> {
    return this.httpClient.get<NumeronCollectionModel>(
      `${this.apiBaseUrl}/numerons/${collectionId}`,
    );
  }

  public getLatestNumeronCollections(
    offset: number,
    limit: number,
  ): Observable<NumeronCollectionModel[]> {
    return this.httpClient.get<NumeronCollectionModel[]>(`${this.apiBaseUrl}/numerons`, {
      params: {
        sort: 'latest',
        offset,
        limit,
      },
    });
  }

  public getMostViewedNumeronCollections(
    offset: number,
    limit: number,
  ): Observable<NumeronCollectionModel[]> {
    return this.httpClient.get<NumeronCollectionModel[]>(`${this.apiBaseUrl}/numerons`, {
      params: {
        sort: 'mostviewed',
        offset,
        limit,
      },
    });
  }

  public getByTopicNumeronCollections(
    topicId: string,
    offset: number,
    limit: number,
  ): Observable<NumeronCollectionModel[]> {
    return this.httpClient.get<NumeronCollectionModel[]>(
      `${this.apiBaseUrl}/topics/${topicId}/numerons`,
      {
        params: {
          sort: 'latest',
          offset,
          limit,
        },
      },
    );
  }

  public getByPublisherNumeronCollections(
    publisherId: string,
    offset: number,
    limit: number,
  ): Observable<NumeronCollectionModel[]> {
    return this.httpClient.get<NumeronCollectionModel[]>(
      `${this.apiBaseUrl}/publishers/${publisherId}/numerons`,
      {
        params: {
          sort: 'latest',
          offset,
          limit,
        },
      },
    );
  }

  public getByCategoryNumeronCollections(
    category: string,
    offset: number,
    limit: number,
  ): Observable<NumeronCollectionModel[]> {
    return this.httpClient.get<NumeronCollectionModel[]>(`${this.apiBaseUrl}/numerons`, {
      params: {
        sort: 'latest',
        offset,
        limit,
        categories: category,
      },
    });
  }

  public getSearchNumeronCollections(
    query: string,
    offset: number,
    limit: number,
  ): Observable<NumeronCollectionModel[]> {
    return this.httpClient.get<NumeronCollectionModel[]>(`${this.apiBaseUrl}/numerons/search`, {
      params: {
        sort: 'latest',
        offset,
        limit,
        query,
      },
    });
  }

  public getMyNumeronCollections(
    offset: number,
    limit: number,
  ): Observable<NumeronCollectionModel[]> {
    return this.httpClient.get<NumeronCollectionModel[]>(`${this.apiBaseUrl}/user/numerons`, {
      params: {
        sort: 'latest',
        offset,
        limit,
      },
    });
  }

  public getMyNumeronCollection(collectionId: string): Observable<NumeronCollectionModel> {
    return this.httpClient.get<NumeronCollectionModel>(
      `${this.apiBaseUrl}/user/numerons/${collectionId}`,
    );
  }

  public updateNumeron(payload: UpdateNumeronPayload): Observable<NumeronCollectionModel> {
    return this.httpClient.put<NumeronCollectionModel>(
      `${this.apiBaseUrl}/user/numerons/${payload.collectionId}`,
      buildUpdateNumeronFormData(payload),
    );
  }

  public createNumeron(payload: CreateNumeronPayload): Observable<NumeronCollectionModel> {
    return this.httpClient.post<NumeronCollectionModel>(
      `${this.apiBaseUrl}/user/numerons`,
      buildCreateNumeronFormData(payload),
    );
  }

  public createCollection(payload: CreateCollectionPayload): Observable<NumeronCollectionModel> {
    return this.httpClient.post<NumeronCollectionModel>(
      `${this.apiBaseUrl}/user/numerons`,
      buildCreateCollectionFormData(payload),
    );
  }

  public updateCollection(payload: UpdateCollectionPayload): Observable<NumeronCollectionModel> {
    return this.httpClient.put<NumeronCollectionModel>(
      `${this.apiBaseUrl}/user/numerons/${payload.collectionId}/numeron/${payload.numeronId}`,
      buildUpdateCollectionFormData(payload),
    );
  }

  public addNumeronToCollection(
    payload: AddNumeronToCollectionPayload,
  ): Observable<NumeronCollectionModel> {
    return this.httpClient.put<NumeronCollectionModel>(
      `${this.apiBaseUrl}/user/numerons/${payload.collectionId}/numeron`,
      buildAddNumeronToCollectionFormData(payload),
    );
  }

  public updateCollectionNumeron(
    payload: UpdateCollectionNumeronPayload,
  ): Observable<NumeronCollectionModel> {
    return this.httpClient.put<NumeronCollectionModel>(
      `${this.apiBaseUrl}/user/numerons/${payload.collectionId}/numeron/${payload.numeronId}`,
      buildAddNumeronToCollectionFormData(payload),
    );
  }

  public deleteMyNumeron(
    collectionId: string,
  ): Observable<{ n: number; ok: number; deletedCount?: number }> {
    return this.httpClient.delete<{ n: number; ok: number; deletedCount?: number }>(
      `${this.apiBaseUrl}/user/numerons/${collectionId}`,
    );
  }

  public deleteCollectionNumeron(
    collectionId: string,
    numeronId: string,
  ): Observable<{ n: number; ok: number; deletedCount?: number }> {
    return this.httpClient.delete<{ n: number; ok: number; deletedCount?: number }>(
      `${this.apiBaseUrl}/user/numerons/${collectionId}/numeron/${numeronId}`,
    );
  }

  public copyMyNumeron(collectionId: string): Observable<NumeronCollectionModel> {
    return this.httpClient.put<NumeronCollectionModel>(
      `${this.apiBaseUrl}/user/numerons/${collectionId}/copy`,
      {},
    );
  }

  public copyCollection(collectionId: string): Observable<NumeronCollectionModel> {
    return this.httpClient.put<NumeronCollectionModel>(
      `${this.apiBaseUrl}/user/numerons/${collectionId}/copy`,
      {},
    );
  }

  public getMyNumeronCollectionsByTopic(
    topicId: string,
    offset: number,
    limit: number,
  ): Observable<NumeronCollectionModel[]> {
    return this.httpClient.get<NumeronCollectionModel[]>(`${this.apiBaseUrl}/user/numerons`, {
      params: {
        sort: 'latest',
        offset,
        limit,
        'topic-id': topicId,
      },
    });
  }

  public getMyNumeronCollectionsByCategory(
    category: string,
    offset: number,
    limit: number,
  ): Observable<NumeronCollectionModel[]> {
    return this.httpClient.get<NumeronCollectionModel[]>(`${this.apiBaseUrl}/user/numerons`, {
      params: {
        sort: 'latest',
        offset,
        limit,
        categories: category,
      },
    });
  }

  public likeCollection(collectionId: string): Observable<{ nModified: number }> {
    return this.httpClient.put<{ nModified: number }>(
      `${this.apiBaseUrl}/numerons/${collectionId}/like`,
      {},
    );
  }

  public likeNumeron(collectionId: string, numeronId: string): Observable<{ nModified: number }> {
    return this.httpClient.put<{ nModified: number }>(
      `${this.apiBaseUrl}/numerons/${collectionId}/numeron/${numeronId}/like`,
      {},
    );
  }
}
